import {
  SPIN_DECAY_TAU,
  SPIN_DECAY_V_REF,
  SPIN_DECAY_V_MIN,
  SPIN_DECAY_RHO_REF,
} from '../constants';

/**
 * Simple exponential spin decay (legacy — used for backward compat).
 * spin(t) = spin_0 * exp(-t / tau)
 */
export function decayedSpinRate(initialSpinRpm: number, timeSeconds: number): number {
  return initialSpinRpm * Math.exp(-timeSeconds / SPIN_DECAY_TAU);
}

/**
 * Compute effective spin decay time constant based on current airspeed and air density.
 *
 * Physics: aerodynamic torque on the ball is proportional to (ρ × v²).
 * Higher airspeed or denser air → more torque → faster spin decay.
 *
 *   tau_eff = TAU_BASE × (V_REF / max(airspeed, V_MIN)) × (RHO_REF / airDensity)
 *
 * At reference conditions (airspeed=55 m/s, ρ=1.225): tau_eff = TAU_BASE = 20s.
 *
 * Behavior across a typical driver flight:
 *   Launch (75 m/s): tau_eff ≈ 14.7s — spin decays ~36% faster
 *   Mid-flight (50 m/s): tau_eff ≈ 22s — near baseline
 *   Near landing (30 m/s): tau_eff ≈ 36.7s — spin decays ~45% slower
 *
 * This gives: lower apex (less spin at peak), steeper landing (more spin retained late).
 *
 * @param airspeed - Current ball airspeed in m/s
 * @param airDensity - Current air density in kg/m³ (default: 1.225)
 * @returns Effective tau in seconds
 */
export function effectiveSpinTau(airspeed: number, airDensity: number = SPIN_DECAY_RHO_REF): number {
  const speedFactor = SPIN_DECAY_V_REF / Math.max(airspeed, SPIN_DECAY_V_MIN);
  const densityFactor = SPIN_DECAY_RHO_REF / Math.max(airDensity, 0.5);
  return SPIN_DECAY_TAU * speedFactor * densityFactor;
}

/**
 * Compute instantaneous spin decay rate: dSpin/dt = -spin / tau_eff
 *
 * Used by the ODE integrator when spin is part of the state vector.
 *
 * @param currentSpinRpm - Current spin rate in RPM
 * @param airspeed - Current ball airspeed in m/s
 * @param airDensity - Air density in kg/m³
 * @returns Rate of change of spin (RPM/s, always negative)
 */
export function spinDecayDerivative(
  currentSpinRpm: number,
  airspeed: number,
  airDensity: number = SPIN_DECAY_RHO_REF,
): number {
  const tau = effectiveSpinTau(airspeed, airDensity);
  return -currentSpinRpm / tau;
}
